/**
 * Literafan Notification History Service
 * Gerencia o histórico de notificações de novas publicações, capítulos e avisos
 * para membros da comunidade e inscritos na newsletter com push ativado.
 */

import { Article } from '../types';

export interface NotificationHistoryItem {
  id: string;
  title: string;
  body: string;
  url: string;
  imageUrl?: string;
  category: 'article' | 'chapter' | 'community' | 'welcome' | 'system';
  categoryLabel: string;
  date: string;
  timestamp: number;
  read: boolean;
}

const STORAGE_KEY = 'literafan_notifications_history_v2';
const EVENT_NAME = 'literafan:notification_history_updated';

// Notificações padrão de curadoria para preencher o histórico inicial
const INITIAL_NOTIFICATIONS: NotificationHistoryItem[] = [
  {
    id: 'init_notif_et_bilu_3',
    title: 'Capítulo 3: A Pedra Fundamental de Ratanabá 🏛️',
    body: 'Estreia programada da saga de ficção científica do ET Bilú. Acompanhe a contagem regressiva oficial!',
    url: '/comunidade?subtab=booklists',
    imageUrl: 'https://i.postimg.cc/9Q5fC0fL/Capa-ET-Bilu-Capitulo-3.png',
    category: 'chapter',
    categoryLabel: 'BOOK LIST',
    date: '3 de out.',
    timestamp: Date.now() - 1000 * 60 * 60 * 2, // 2 horas atrás
    read: false
  },
  {
    id: 'init_notif_et_bilu_2',
    title: 'Capítulo 2: Protocolo Café com Pão de Queijo ☕',
    body: 'Novo capítulo publicado! Bilú da Silva descobre os segredos da hospitalidade e cafezinhos da Amazônia.',
    url: '/comunidade?subtab=booklists',
    imageUrl: 'https://i.postimg.cc/k47tXh8G/Capa-ET-Bilu-Capitulo-2.png',
    category: 'chapter',
    categoryLabel: 'BOOK LIST',
    date: '26 de set.',
    timestamp: Date.now() - 1000 * 60 * 60 * 72, // 3 dias atrás
    read: false
  },
  {
    id: 'init_notif_welcome',
    title: 'Bem-vindo(a) ao feed de Alertas do Literafan! 🔔',
    body: 'Inscrição ativa. Você receberá avisos em tempo real sobre lançamentos de artigos, resenhas e capítulos das Book Lists.',
    url: '/artigos',
    imageUrl: 'https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png',
    category: 'welcome',
    categoryLabel: 'BOAS-VINDAS',
    date: 'Recente',
    timestamp: Date.now() - 1000 * 60 * 60 * 96,
    read: true
  }
];

const dispatchHistoryUpdate = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
};

/**
 * Retorna todas as notificações armazenadas
 */
export const getNotificationHistory = (): NotificationHistoryItem[] => {
  if (typeof window === 'undefined') return INITIAL_NOTIFICATIONS;

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_NOTIFICATIONS));
      return INITIAL_NOTIFICATIONS;
    }
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : INITIAL_NOTIFICATIONS;
  } catch {
    return INITIAL_NOTIFICATIONS;
  }
};

/**
 * Salva a lista de notificações
 */
const saveNotificationHistory = (items: NotificationHistoryItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    dispatchHistoryUpdate();
  } catch (err) {
    console.warn('Erro ao salvar histórico de notificações:', err);
  }
};

/**
 * Adiciona uma nova notificação ao histórico
 */
export const addNotificationToHistory = (
  item: Omit<NotificationHistoryItem, 'id' | 'timestamp' | 'read' | 'date'> & {
    id?: string;
    timestamp?: number;
    read?: boolean;
    date?: string;
  }
): NotificationHistoryItem => {
  const current = getNotificationHistory();
  const id = item.id || `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  
  // Se já existe com este ID, atualiza
  const existingIndex = current.findIndex(n => n.id === id);
  const now = item.timestamp || Date.now();
  
  const newItem: NotificationHistoryItem = {
    id,
    title: item.title,
    body: item.body,
    url: item.url || '/artigos',
    imageUrl: item.imageUrl,
    category: item.category || 'article',
    categoryLabel: item.categoryLabel || (item.category === 'chapter' ? 'BOOK LIST' : item.category === 'community' ? 'COMUNIDADE' : 'ARTIGO'),
    date: item.date || 'Há pouco',
    timestamp: now,
    read: item.read ?? false
  };

  let updated: NotificationHistoryItem[];
  if (existingIndex >= 0) {
    updated = [...current];
    updated[existingIndex] = { ...updated[existingIndex], ...newItem };
  } else {
    // Mantém no máximo 50 notificações mais recentes
    updated = [newItem, ...current].slice(0, 50);
  }

  saveNotificationHistory(updated);
  return newItem;
};

/**
 * Marca uma notificação como lida
 */
export const markNotificationAsRead = (id: string) => {
  const current = getNotificationHistory();
  const updated = current.map(item => (item.id === id ? { ...item, read: true } : item));
  saveNotificationHistory(updated);
};

/**
 * Marca todas as notificações como lidas
 */
export const markAllNotificationsAsRead = () => {
  const current = getNotificationHistory();
  const updated = current.map(item => ({ ...item, read: true }));
  saveNotificationHistory(updated);
};

/**
 * Remove uma notificação específica
 */
export const deleteNotificationFromHistory = (id: string) => {
  const current = getNotificationHistory();
  const updated = current.filter(item => item.id !== id);
  saveNotificationHistory(updated);
};

/**
 * Limpa todo o histórico de notificações
 */
export const clearNotificationHistory = () => {
  saveNotificationHistory([]);
};

/**
 * Retorna a quantidade de notificações não lidas
 */
export const getUnreadNotificationsCount = (): number => {
  const current = getNotificationHistory();
  return current.filter(item => !item.read).length;
};

/**
 * Sincroniza artigos recentes com o histórico de notificações
 */
export const syncArticlesToNotificationHistory = (articles: Article[]) => {
  if (!articles || articles.length === 0) return;
  const current = getNotificationHistory();

  // Pega os 4 artigos mais recentes
  const recentArticles = articles.slice(0, 4);

  let hasNew = false;
  const updated = [...current];

  for (const art of recentArticles) {
    const notifId = `article_${art.id}`;
    const exists = updated.some(n => n.id === notifId || n.url?.includes(art.slug || art.id));
    
    if (!exists) {
      hasNew = true;
      updated.unshift({
        id: notifId,
        title: art.title,
        body: art.excerpt || 'Novo artigo publicado na curadoria oficial do Literafan.',
        url: `/artigo/${art.slug || art.id}`,
        imageUrl: art.imageUrl,
        category: 'article',
        categoryLabel: Array.isArray(art.category) ? art.category[0] : (art.category || 'ARTIGO'),
        date: art.date || 'Recente',
        timestamp: Date.now(),
        read: false
      });
    }
  }

  if (hasNew) {
    saveNotificationHistory(updated.slice(0, 50));
  }
};
